import { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle2, ShieldAlert, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import {
  MailTargetNotice,
  mailAckSentence,
  useMailTarget,
} from '@/components/runs/MailTargetNotice';
import type { BatchCounts } from './batchSelection';

/**
 * Schritt 3 der Komplettwartung: ehrliche Zusammenfassung.
 *
 * Der Nutzer denkt "warte diesen Kunden", das Tool startet aber N Läufe -
 * einen je Routine. Genau das steht hier im Klartext, inklusive der
 * Doppelzählung, wenn ein System zu mehreren Routinen passt.
 *
 * `rows` kommt in Ausführungsreihenfolge herein (Server-Reihenfolge der
 * Vorschau-Gruppen) und wird hier bewusst NICHT umsortiert - die Nummerierung
 * ist die letzte Gelegenheit, ein "apply vor check" zu bemerken.
 */

export interface BatchConfirmRow {
  routineSlug: string;
  displayName: string;
  systemCount: number;
  destructive: boolean;
}

interface BatchConfirmStepProps {
  customerName: string;
  rows: BatchConfirmRow[];
  counts: BatchCounts;
  mode: 'check' | 'apply';
  snapshotEnabled: boolean;
  /** Begründeter Snapshot-Verzicht (Migration 0029), gilt für den ganzen
   *  Batch; null = kein Verzicht fällig. */
  snapshotWaiverReason?: string | null;
  snapshotWaiverEvidence?: string | null;
  /** true = Verzicht fällig, aber unvollständig -> Start gesperrt. */
  startDisabled?: boolean;
  idempotencyKey: string;
  onPrev: () => void;
  onStart: () => void;
  starting: boolean;
  startError: Error | null;
}

export function BatchConfirmStep({
  customerName,
  rows,
  counts,
  mode,
  snapshotEnabled,
  snapshotWaiverReason,
  snapshotWaiverEvidence,
  startDisabled,
  idempotencyKey,
  onPrev,
  onStart,
  starting,
  startError,
}: BatchConfirmStepProps): JSX.Element {
  const isApply = mode === 'apply';
  const [confirmed, setConfirmed] = useState(!isApply);
  const mailTarget = useMailTarget();
  const mailAck = mailAckSentence(mailTarget, customerName);

  // Mode-Wechsel setzt die Bestätigung zurück (wie im Einzel-Wizard).
  useEffect(() => {
    setConfirmed(!isApply);
  }, [isApply]);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <span>Bestätigen + Starten</span>
          {isApply && <Badge tone="danger">Echtlauf</Badge>}
        </span>
      }
      description={`Komplettwartung für ${customerName}.`}
    >
      <div className="flex flex-col gap-3">
        <p
          className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm"
          data-testid="batch-summary-line"
        >
          Es {counts.runs === 1 ? 'wird' : 'werden'} <strong>{counts.runs}</strong>{' '}
          {counts.runs === 1 ? 'Lauf' : 'Läufe'} gestartet (einer je Routine),
          zusammen <strong>{counts.systems}</strong>{' '}
          {counts.systems === 1 ? 'System' : 'Systeme'}.
          {counts.assignments !== counts.systems && (
            <>
              {' '}
              {counts.assignments} Systemzuweisungen &mdash; Systeme, auf die mehrere
              Routinen passen, werden mehrfach angefasst.
            </>
          )}
        </p>

        <div className="flex flex-col gap-1 border-b border-border pb-2 text-sm">
          <span className="text-xs uppercase tracking-wider text-muted">
            Laeufe ({rows.length}) in dieser Reihenfolge
            {isApply && <span className="ml-1 text-danger">&ndash; verändern real</span>}
          </span>
          <ol className="flex flex-col gap-1" data-testid="batch-confirm-rows">
            {rows.map((row, index) => (
              <li
                key={row.routineSlug}
                className="flex flex-wrap items-center gap-2"
                data-testid={`batch-confirm-row-${row.routineSlug}`}
              >
                <span
                  className="flex size-5 shrink-0 items-center justify-center rounded-full border border-border bg-surface-2 text-[10px] font-semibold tabular-nums"
                  title={`Schritt ${index + 1} von ${rows.length}`}
                  data-testid={`batch-confirm-position-${row.routineSlug}`}
                >
                  {index + 1}
                </span>
                <span className="font-medium">{row.displayName}</span>
                <code className="font-mono text-xs text-muted">{row.routineSlug}</code>
                <span className="text-xs text-muted">
                  &middot; {row.systemCount} {row.systemCount === 1 ? 'System' : 'Systeme'}
                </span>
                {row.destructive && <Badge tone="danger">destruktiv</Badge>}
              </li>
            ))}
          </ol>
        </div>

        <SummaryRow
          label="Mode"
          value={mode === 'check' ? 'Dry-Run (--check)' : 'Echtlauf (apply)'}
        />
        <SummaryRow label="Pre-Snapshot" value={snapshotEnabled ? 'ja' : 'nein'} />
        {/* Der Verzicht gilt für JEDEN Lauf des Batches - deshalb steht er
            wörtlich in der letzten Zusammenfassung vor dem Start. */}
        {snapshotWaiverReason != null && snapshotWaiverReason !== '' && (
          <div
            className="flex flex-col gap-1 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
            data-testid="batch-confirm-waiver"
          >
            <span className="font-medium text-warning">
              Snapshot-Verzicht mit Begründung (gilt für alle Läufe)
            </span>
            <span>{snapshotWaiverReason}</span>
            {snapshotWaiverEvidence != null && snapshotWaiverEvidence !== '' && (
              <span className="text-xs text-muted">
                Ersatzsicherung: {snapshotWaiverEvidence}
              </span>
            )}
          </div>
        )}
        <SummaryRow label="Idempotency-Key" value={idempotencyKey} mono />

        {/* Direkt vor Checkbox + Start-Knopf: das Mailziel ist das Letzte,
            was vor dem Klick gelesen wird. */}
        <MailTargetNotice mode={mode} customerName={customerName} />

        {isApply && (
          <label className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/5 p-3 text-sm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 size-4 accent-danger"
              data-testid="batch-confirm-destructive"
            />
            <span>
              Ich verstehe, dass hier{' '}
              <strong>
                {counts.runs} {counts.runs === 1 ? 'Lauf' : 'Läufe'}
              </strong>{' '}
              gegen{' '}
              <strong>
                {counts.systems} {counts.systems === 1 ? 'reales System' : 'reale Systeme'}
              </strong>{' '}
              von {customerName} {counts.runs === 1 ? 'startet' : 'starten'}.
              {!snapshotEnabled && ' Ohne Snapshot ist kein Rollback möglich.'}
              {mailAck}
            </span>
          </label>
        )}

        {startError && <ErrorAlert error={startError} />}
      </div>

      <footer className="mt-4 flex justify-between">
        <Button variant="secondary" leftIcon={<ArrowLeft className="size-4" />} onClick={onPrev}>
          Zurück
        </Button>
        <Button
          variant={isApply ? 'danger' : 'primary'}
          leftIcon={
            isApply ? <ShieldAlert className="size-4" /> : <Sparkles className="size-4" />
          }
          onClick={onStart}
          loading={starting}
          disabled={!confirmed || counts.runs === 0 || Boolean(startDisabled)}
          data-testid="batch-start"
          size="lg"
        >
          {isApply
            ? `${counts.runs} ${counts.runs === 1 ? 'Echtlauf' : 'Echtläufe'} starten`
            : `${counts.runs} ${counts.runs === 1 ? 'Dry-Run' : 'Dry-Runs'} starten`}
        </Button>
      </footer>

      <p className="mt-2 inline-flex items-center gap-1 text-xs text-muted">
        <CheckCircle2 className="size-3" aria-hidden="true" /> Danach zeigt das Tool jeden
        erzeugten Lauf einzeln &mdash; es wird nicht automatisch weitergeleitet.
      </p>
    </Card>
  );
}

function SummaryRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border pb-2 text-sm last:border-b-0">
      <span className="min-w-[140px] text-xs uppercase tracking-wider text-muted">
        {label}
      </span>
      <span className={mono ? 'font-mono text-xs' : ''}>{value}</span>
    </div>
  );
}
